import React, {
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { notification } from 'antd';

import RegionsContext from '@totem/components/portfolio/regions/list/RegionsContext';
import {
  AddEditRegion,
  RegionSummaryItem,
} from '@totem/components/portfolio/regions/types';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { Params } from '@totem/types/common';
import { V2_REGIONS_ENDPOINT } from '@totem/utilities/endpoints';
import { CheckResponseShowError } from '@totem/utilities/responseUtilities';

type Props = {
  children?: ReactNode;
};

const EMPTY_REGION: AddEditRegion = { name: '' };

const RegionsContainer = ({ children }: Props) => {
  const { userProfile, getApiToken } = useContext(UserProfileContext);

  const [loading, setLoading] = useState<boolean>(true);
  const [sending, setSending] = useState<boolean>(false);
  const [refreshData, setRefreshData] = useState<boolean>(true);
  const [data, setData] = useState<RegionSummaryItem[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>('');

  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [editingRegion, setEditingRegion] =
    useState<AddEditRegion>(EMPTY_REGION);

  useEffect(() => {
    setRefreshData(true);
  }, [userProfile.organization.id]);

  useEffect(() => {
    if (!refreshData) {
      return;
    }
    setRefreshData(false);

    setLoading(true);

    const payload: Params = {
      paging: { page: 0, pageSize: 0 },
      sort: { field: 'name', direction: 1 },
      filters: searchTerm
        ? [{ field: 'name', operator: 'cscontains', values: [searchTerm] }]
        : [],
    };

    fetch(V2_REGIONS_ENDPOINT, {
      method: 'POST',
      headers: new Headers({
        'Content-Type': 'application/json',
        Authorization: `Bearer ${getApiToken()}`,
      }),
      body: JSON.stringify(payload),
    })
      .then((res) => res.json())
      .then((result: RegionSummaryItem[]) => {
        setData(Array.isArray(result) ? result : []);
      })
      .then(() => {
        setLoading(false);
      });
  }, [refreshData, searchTerm, getApiToken]);

  const handleRefresh = useCallback(() => {
    setRefreshData(true);
  }, []);

  const createRegion = useCallback(
    (region: AddEditRegion) => {
      setSending(true);

      fetch(V2_REGIONS_ENDPOINT, {
        method: 'PUT',
        headers: new Headers({
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getApiToken()}`,
        }),
        body: JSON.stringify({ name: region.name }),
      }).then((res) => {
        setSending(false);

        if (CheckResponseShowError(res)) {
          notification.success({
            message: 'Region created',
            description: `"${region.name}" has been created.`,
          });

          setIsModalOpen(false);

          setRefreshData(true);
        }
      });
    },
    [getApiToken],
  );

  const updateRegion = useCallback(
    (region: AddEditRegion) => {
      setSending(true);

      fetch(`${V2_REGIONS_ENDPOINT}/${region.id}`, {
        method: 'PATCH',
        headers: new Headers({
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getApiToken()}`,
        }),
        body: JSON.stringify({ regionID: region.id, name: region.name }),
      }).then((res) => {
        setSending(false);

        if (CheckResponseShowError(res)) {
          notification.success({
            message: 'Region updated',
            description: `"${region.name}" has been updated.`,
          });

          setIsModalOpen(false);

          setRefreshData(true);
        }
      });
    },
    [getApiToken],
  );

  const deleteRegion = useCallback(
    (region: RegionSummaryItem) => {
      setSending(true);

      fetch(`${V2_REGIONS_ENDPOINT}/${region.id}`, {
        method: 'DELETE',
        headers: new Headers({
          Authorization: `Bearer ${getApiToken()}`,
        }),
      }).then((res) => {
        setSending(false);

        if (CheckResponseShowError(res)) {
          notification.success({
            message: 'Region deleted',
            description: `"${region.name}" has been deleted.`,
          });

          setRefreshData(true);
        }
      });
    },
    [getApiToken],
  );

  const handleAction = useCallback(
    (action: string, region?: RegionSummaryItem) => {
      switch (action) {
        case 'add':
          setIsEdit(false);

          setEditingRegion(EMPTY_REGION);

          setIsModalOpen(true);
          break;
        case 'edit':
          setIsEdit(true);

          setEditingRegion({ id: region.id, name: region.name });

          setIsModalOpen(true);
          break;
        case 'delete':
          deleteRegion(region);
          break;
        case 'refresh':
          handleRefresh();
          break;
        default:
          break;
      }
    },
    [deleteRegion, handleRefresh],
  );

  const handleSubmit = useCallback(
    (region: AddEditRegion) => {
      if (isEdit) {
        updateRegion(region);
      } else {
        createRegion(region);
      }
    },
    [isEdit, createRegion, updateRegion],
  );

  return (
    <RegionsContext.Provider
      value={{
        loading,
        sending,
        data,
        totalRecords: data.length,
        searchTerm,
        setSearchTerm,
        isModalOpen,
        isEdit,
        editingRegion,
        onAction: handleAction,
        onSubmit: handleSubmit,
        onCloseModal: () => setIsModalOpen(false),
      }}
    >
      {children}
    </RegionsContext.Provider>
  );
};

export default RegionsContainer;
